import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Space, Table } from 'antd';

import ExportEntitiesButton from '../../../components/buttons/ExportEntitiesButton';
import PageTitle from '../../../components/PageTitle';
import useDatasource from '../../../hooks/useDatasource';
import {
  downloadInternalCorrections,
  getInternalCorrections,
} from '../../../services/accounts/internal-corrections';
import Column from '../../../helpers/columns';
import { Environments } from '../../../helpers/constants';
import { getAdminUnits } from '../../../services/taxonomies/zonal-restriction';

export const TransactionType = {
  content: [{ id: 'DELETE' }, { id: 'UPDATE_UATS' }, { id: 'UPDATE_LINES' }],
  loading: false,
  error: false,
};

const DATE_STATUS = {
  content: [{ id: 0 }, { id: 1 }, { id: 2 }],
  loading: false,
  error: false,
};

export const CustomerTypes = {
  content: [
    { id: 'Cerere', name: 'Cerere' },
    { id: 'Corecție', name: 'Corecție' },
  ],
};

export const ProductCategories = {
  content: [
    { id: 'PASS' },
    { id: 'PURSE' },
    { id: 'TICKET' },
    { id: 'OPERATOR' },
  ],
  loading: false,
  error: false,
};

const InternalCorrectionGrid = () => {
  const { t } = useTranslation();

  const { pagination, sort, criterias, content, handleChange } = useDatasource(
    getInternalCorrections,
  );

  const environment = useMemo(() => window._env_.ENVIROMENT, []);
  const [uatsDictionary, setUatsDictionary] = useState({
    content: [],
    loading: false,
    error: false,
  });

  const metropolitanEnabled = useMemo(
    () => window._env_.METROPOLITAN_ENABLE === 'true',
    [],
  );

  useEffect(() => {
    let cancelled = false;

    const shouldLoadUats =
      metropolitanEnabled && environment === Environments.SIBIU;

    if (!shouldLoadUats) {
      setUatsDictionary({
        content: [],
        loading: false,
        error: false,
      });

      return () => {
        cancelled = true;
      };
    }

    setUatsDictionary({
      content: [],
      loading: true,
      error: false,
    });

    const loadUats = async () => {
      try {
        const data = await getAdminUnits();
        if (!cancelled) {
          setUatsDictionary({
            content: data,
            loading: false,
            error: false,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setUatsDictionary({
            content: [],
            loading: false,
            error: true,
          });
        }
      }
    };

    loadUats();

    return () => {
      cancelled = true;
    };
  }, [metropolitanEnabled, environment]);

  const columns = useMemo(
    () => [
      Column.text('id', t('entity.internalCorrection.id'), {
        filter: false,
        width: 50,
      }),
      Column.date('createdAt', t('entity.internalCorrection.createdAt'), {
        filter: true,
      }),
      Column.text('accountId', t('entity.internalCorrection.accountId'), {
        filter: true,
        width: 50,
        sort: false,
      }),
      Column.dictionary(
        'transactionType',
        t('entity.internalCorrection.transactionType'),
        TransactionType,
        {
          filter: true,
          width: 150,
          dictLabel: (entry) => t(`entity.internalCorrection.${entry.id}`),
          sort: false,
        },
      ),
      Column.dictionary(
        'productCategory',
        t('entity.internalCorrection.productCategory._'),
        ProductCategories,
        {
          filter: true,
          width: 150,
          dictLabel: (entry) =>
            t(`entity.internalCorrection.productCategory.${entry.id}`),
          sort: false,
        },
      ),
      Column.text('parentProdId', t('entity.internalCorrection.parentProdId'), {
        filter: true,
      }),
      Column.text(
        'parentProdName',
        t('entity.internalCorrection.parentProdName'),
        {
          filter: true,
        },
      ),
      Column.text(
        'parentProdQty',
        t('entity.internalCorrection.parentProdQty'),
        {
          filter: true,
          sort: false,
        },
      ),
      Column.dictionary(
        'dateStatus',
        t('entity.internalCorrection.dateStatus._'),
        DATE_STATUS,
        {
          width: 150,
          filter: true,
          sort: false,
          dictLabel: (entry) =>
            t(`entity.internalCorrection.dateStatus.${entry.id}`),
        },
      ),
      Column.date(
        'startDateTime',
        t('entity.internalCorrection.startDateTime'),
        {
          filter: false,
          sort: false,
          width: 150,
        },
      ),

      Column.date('endDateTime', t('entity.internalCorrection.endDateTime'), {
        filter: false,
        sort: false,
        width: 150,
      }),
      Column.dictionary(
        'disableReason',
        t('entity.internalCorrection.disableReason'),
        CustomerTypes,
        {
          width: 150,
          filter: true,
          sort: false,
        },
      ),
      ...(environment !== Environments.SIBIU
        ? [
            Column.text('lines', t('entity.internalCorrection.lines'), {
              filter: true,
              sort: false,
            }),
          ]
        : []),
      ...(environment === Environments.SIBIU
        ? [
            Column.dictionary(
              'uats',
              t('entity.internalCorrection.uats'),
              uatsDictionary,
              {
                width: 150,
                filter: true,
                sort: false,
                separator: ',',
              },
            ),
          ]
        : []),
    ],
    [t, environment, uatsDictionary],
  );

  const exportColumns = useMemo(
    () =>
      columns.map((column) =>
        column.dataIndex === 'dateStatus' || column.key === 'dateStatus'
          ? {
              ...column,
              key: 'dateStatusName',
              dataIndex: 'dateStatusName',
            }
          : column,
      ),
    [columns],
  );

  return (
    <>
      <PageTitle>
        <Space>
          <ExportEntitiesButton
            downloadFiles={downloadInternalCorrections}
            columns={exportColumns}
            data={{ sort, criterias }}
          />
        </Space>
      </PageTitle>

      <Table
        rowKey="id"
        columns={columns}
        // loading={loading}
        pagination={pagination}
        dataSource={content || []}
        onChange={handleChange}
        scroll={{ x: 'max-content', y: 700 }}
        size="small"
      />
    </>
  );
};

export default InternalCorrectionGrid;
